import { Role } from '@/Enums/Users/Role.enum';
import { isRestrictedAccessRole } from '@/helpers/roles';
import axios from 'axios';

// flank#4749: switching the User Type dropdown no longer wipes a screen's
// form value when its control unmounts (see TripleToggleControl), so a value
// set for a screen that belonged to a since-abandoned User Type can still be
// sitting in `accessManagement` at submit time. `allowedScreenKeys` — the
// CURRENTLY selected role's own screensByRole entry — is the defense-in-depth
// filter that keeps a leftover value from a different role out of the saved
// payload, same convention as the restricted-tier DSP/Spend nulling below.
// Omitted (undefined), it passes every truthy entry through unfiltered.
const getScreens = (accessManagement, allowedScreenKeys) => {
  const screens = [];
  Object.entries(accessManagement).forEach(([key, value]) => {
    if (!value) return;
    if (allowedScreenKeys && !allowedScreenKeys.includes(key)) return;
    screens.push({
      name: key,
      access: value === true ? 'full' : value,
    });
  });

  return screens;
};

// Resolves the screen keys a given role may currently hold, from Redux's
// `screensByRole` (see AccessManagement/index.jsx, the same source it reads
// from to render the grid). Returns undefined — "don't filter" — for a role
// screensByRole hasn't loaded yet, rather than wiping every screen out.
export const getAllowedScreenKeys = (screensByRole, role) => screensByRole?.[+role]?.map(screen => screen.value);

// Pure gate extracted for testability: both modals' submit buttons must
// disable while allowedScreenKeys is undefined (mapping missing/not yet
// loaded for the selected role), since getScreens treats undefined as "no
// filter" and would submit unfiltered form state. An empty array is a valid,
// already-resolved "zero allowed screens" and must NOT block submission.
export const isScreenAccessSubmitDisabled = allowedScreenKeys => allowedScreenKeys === undefined;

export const getUserPayload = (formData, allowedScreenKeys) => {
  const {
    email,
    firstName: first_name,
    lastName: last_name,
    phone: phone_number,
    userRole,
    accessManagement,
    organization,
    whitelabel,
    clients,
    client,
    advertiser,
    agency,
    canApproveCampaigns: can_approve_campaigns,
    canViewDspPlatform: can_view_dsp_platform,
    canViewWholesaleSpendCpm: can_view_wholesale_spend_cpm,
    canViewClientSpendCpm: can_view_client_spend_cpm,
    canViewRawInventory: can_view_raw_inventory,
    canViewAttribution: can_view_attribution,
  } = formData;

  const whitelabel_id = whitelabel?.id;

  const role = +userRole;

  const screens = getScreens(accessManagement, allowedScreenKeys);

  // Defense in depth for #4444's "never, no override" rule: Client/Agency/
  // Advertiser must never carry a DSP/Platform or wholesale Spend/CPM
  // override, even if stale UI/form state has one, since the toggles that
  // set these are hidden (not unmounted) for these roles.
  const restricted = isRestrictedAccessRole(role);

  let payload = {
    email,
    first_name,
    last_name,
    phone_number,
    screens,
    client_ids: [],
    agency_id: null,
    advertiser_id: null,
    role_id: role,
    whitelabel_id,
    can_approve_campaigns,
    can_view_dsp_platform: restricted ? null : can_view_dsp_platform,
    can_view_wholesale_spend_cpm: restricted ? null : can_view_wholesale_spend_cpm,
    can_view_client_spend_cpm,
    can_view_raw_inventory,
    can_view_attribution,
  };
  switch (role) {
    case Role.ORG_ADMIN:
      payload = {
        ...payload,
        whitelabel_id: organization?.id,
      };
      break;
    case Role.WHITELABEL_ADMIN:
      break;
    case Role.CLIENT:
      payload = {
        ...payload,
        client_ids: clients.map(({ value }) => value),
      };
      break;
    case Role.ADVERTISER:
      payload = {
        ...payload,
        advertiser_id: advertiser.value,
        whitelabel_id: client.whitelabel,
      };
      break;
    case Role.AGENCY:
      payload = {
        ...payload,
        agency_id: agency.value,
        whitelabel_id: client.whitelabel,
      };
      break;
    default:
      break;
  }
  return payload;
};

export const updateUser = (formData, allowedScreenKeys) => {
  const { id } = formData;
  const payload = getUserPayload(formData, allowedScreenKeys);

  return axios.put(`/users/${id}`, payload);
};

export const createUser = (formData, allowedScreenKeys) => {
  const payload = getUserPayload(formData, allowedScreenKeys);

  return axios.post('/users', payload);
};

export const deleteUser = id => {
  return axios.delete(`/users/${id}`);
};

export const resetUserPassword = id => {
  return axios.post(`/users/${id}/resetPassword`);
};

export const bulkUpdateScreenAccess = (userIds, screens, access) => {
  return axios.post('/users/bulk/screen-access', { user_ids: userIds, screens, access });
};
